import { FC, ReactNode } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Badge, Box, ButtonBase, GlobalStyles, Typography, useMediaQuery } from '@mui/material'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import BoltIcon from '@mui/icons-material/Bolt'
import { useQuery } from 'react-query'
import { useAuth } from 'context/JWTContext'
import { FlowTypes } from '@/lib/constants'
import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { INSTANT_WINS_FILTER } from '@/utils/constants/prizeDrawConsts'
import { isListedPenaltyShootout } from '@/utils/prizeDrawLists'
import { raffleApi } from 'api/raffleApi'
import { BLUE_COLORS_VARIANTS } from '@/utils/constants/colors'
import { useWalletBalance } from 'hooks/useWalletBalance'
import { ProfileDrawer } from 'components/prize-draws/profile-drawer/ProfileDrawer'
import { PartnerPageData } from 'types/types'
import { IRaffleInList } from 'types/prize-draw/raffles'
import { usePrizeDrawContextStore } from 'store/prizeDrawContextStore'
import { getWalletPath } from '@/utils/walletPath'
import { DESKTOP_QUERY, MOBILE_QUERY } from './bottomNavStyles'

const NAV_BG = '#0F1E27'
const NAV_BORDER = '#1a2c38'
const PRIZE_DRAWS_ACCENT = '#36E0F6'
const PREDICTOR_ACCENT = '#FCAD0E'
const INACTIVE = '#8fa0aa'
const BALANCE = '#F0B040'

const NAV_HEIGHT = 'calc(64px + max(8px, env(safe-area-inset-bottom)))'

const ICON_SX = { fontSize: '22px' }

const PREDICTOR_ROUTES = ['/[slug]/landing', '/[slug]/games', '/[slug]/mini-league']

interface NavSlotProps {
  icon: ReactNode
  label: string
  isActive?: boolean
  accent?: string
  href?: string
  onClick?: () => void
  ariaLabel?: string
  badgeCount?: number
}

const NavSlot: FC<NavSlotProps> = ({
  icon,
  label,
  isActive,
  accent = PRIZE_DRAWS_ACCENT,
  href,
  onClick,
  ariaLabel,
  badgeCount = 0,
}) => {
  return (
    <ButtonBase
      component={href ? Link : 'button'}
      href={href}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        minWidth: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: '3px',
        px: 0,
        borderRadius: '12px',
        border: `1px solid ${isActive ? accent : 'transparent'}`,
        bgcolor: isActive ? `${accent}1A` : 'transparent',
        color: isActive ? accent : INACTIVE,
      }}
    >
      {badgeCount > 0 ? (
        <Badge
          badgeContent={badgeCount}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: BLUE_COLORS_VARIANTS[500],
              border: `2px solid ${NAV_BG}`,
              color: 'white',
            },
          }}
        >
          {icon}
        </Badge>
      ) : (
        icon
      )}
      <Typography
        fontWeight={isActive ? 700 : 500}
        color="white"
        textAlign="center"
        maxWidth="100%"
        sx={{
          fontSize: 'clamp(9px, 2.6vw, 11px)',
          whiteSpace: 'nowrap',
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  )
}

interface MobileBottomNavProps {
  page: PartnerPageData
  initialRaffles?: IRaffleInList[]
}

export const MobileBottomNav: FC<MobileBottomNavProps> = ({ page, initialRaffles }) => {
  const router = useRouter()
  const { user, isAuthenticated, isInitialized } = useAuth()
  const isLoggedIn = isAuthenticated && Boolean(user)
  const balance = useWalletBalance(isLoggedIn)
  const isPrizeDrawContext = usePrizeDrawContextStore((state) => state.isPrizeDrawContext)
  const isMobile = useMediaQuery(MOBILE_QUERY)

  // The server falls back to [] when its raffle fetch fails; seeding the cache with that
  // would hide Instant Wins for the whole staleTime, so only seed a non-empty list.
  const seedRaffles = initialRaffles?.length ? initialRaffles : undefined
  const { data: raffles, isError: isRafflesError } = useQuery(
    [QUERY_KEYS.PARTNER_RAFFLES, page.id],
    () => raffleApi.getPartnerRaffles(page.id),
    {
      staleTime: 5 * 60 * 1000,
      enabled: isMobile,
      initialData: seedRaffles,
      initialDataUpdatedAt: seedRaffles ? Date.now() : undefined,
    },
  )
  const hasInstantWins = Boolean(raffles?.some(isListedPenaltyShootout))
  // Hold the Instant Wins slot until raffles arrive so the bar doesn't jump from 4 to 5 tabs.
  const isInstantWinsPending = raffles === undefined && !isRafflesError
  const showInstantWinsSlot = hasInstantWins || isInstantWinsPending

  const { pathname } = router
  const isInstantWinsActive =
    hasInstantWins &&
    pathname === '/[slug]/prize-draw' &&
    router.query.filter === INSTANT_WINS_FILTER
  const isWalletActive = pathname === '/[slug]/prize-draw/wallet'
  const isPrizeDrawActive =
    !isInstantWinsActive &&
    !isWalletActive &&
    (pathname === '/[slug]' || pathname.startsWith('/[slug]/prize-draw'))
  const isPredictorActive = PREDICTOR_ROUTES.some((route) => pathname.startsWith(route))

  const authHref = (step: 'email-check' | 'register') =>
    `/${page.company_domain}/auth/${
      page.is_external_login ? 'external-login' : step
    }?${new URLSearchParams({
      flowType: FlowTypes.AUTH,
      slug: page.company_domain,
      redirectTo: router.asPath,
    })}`

  const balanceText = balance.format(balance.total)
  let balanceAriaLabel = `Balance ${balanceText}, open wallet`
  if (balance.isLoading) balanceAriaLabel = 'Balance loading, open wallet'
  else if (balance.isUnavailable) balanceAriaLabel = 'Balance unavailable, open wallet'

  const renderAuthSlots = () => {
    if (!isInitialized) return <Box gridColumn="span 2" />

    if (!isLoggedIn) {
      return (
        <>
          <NavSlot
            icon={<LoginOutlinedIcon sx={ICON_SX} />}
            label="Log In"
            href={authHref('email-check')}
          />
          <Box minWidth={0} display="flex" alignItems="center">
            <ButtonBase
              href={authHref('register')}
              LinkComponent={Link}
              sx={{
                width: '100%',
                height: '40px',
                gap: 0.5,
                borderRadius: '999px',
                bgcolor: BALANCE,
                color: NAV_BG,
              }}
            >
              <PersonAddAlt1OutlinedIcon
                sx={{ fontSize: '18px', '@media (max-width: 359.98px)': { display: 'none' } }}
              />
              <Typography fontSize="0.875rem" fontWeight={800}>
                Join
              </Typography>
            </ButtonBase>
          </Box>
        </>
      )
    }

    return (
      <>
        <NavSlot
          icon={
            <Typography
              component="span"
              fontWeight={800}
              color={BALANCE}
              lineHeight="22px"
              noWrap
              maxWidth="100%"
              sx={{ fontSize: 'clamp(11px, 3.4vw, 14px)' }}
            >
              {balanceText}
            </Typography>
          }
          label="Balance"
          href={getWalletPath(page.company_domain)}
          isActive={isWalletActive}
          ariaLabel={balanceAriaLabel}
        />
        <ProfileDrawer
          pageTheme={page}
          isPrizeDrawContext={isPrizeDrawContext}
          renderTrigger={(open, badgeCount) => (
            <NavSlot
              badgeCount={badgeCount}
              icon={<PersonOutlineOutlinedIcon sx={ICON_SX} />}
              label="Account"
              onClick={open}
            />
          )}
        />
      </>
    )
  }

  return (
    <>
      <GlobalStyles
        styles={{
          [MOBILE_QUERY]: {
            body: { '--bottom-nav-height': NAV_HEIGHT, paddingBottom: NAV_HEIGHT },
          },
        }}
      />
      <Box
        component="nav"
        aria-label="Main navigation"
        data-bottom-nav=""
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1150,
          display: 'grid',
          gridTemplateColumns: `repeat(${showInstantWinsSlot ? 5 : 4}, minmax(0, 1fr))`,
          // Even breathing room between slots and from the screen edges.
          // Five slots need the room more than the gaps do.
          gap: showInstantWinsSlot ? '4px' : '8px',
          height: NAV_HEIGHT,
          px: showInstantWinsSlot ? '6px' : '12px',
          pt: 1,
          pb: 'max(8px, env(safe-area-inset-bottom))',
          bgcolor: NAV_BG,
          borderTop: `1px solid ${NAV_BORDER}`,
          [DESKTOP_QUERY]: { display: 'none' },
        }}
      >
        <NavSlot
          icon={<ConfirmationNumberOutlinedIcon sx={ICON_SX} />}
          label="Prize Draws"
          href={`/${page.company_domain}/prize-draw`}
          isActive={isPrizeDrawActive}
        />
        {hasInstantWins && (
          <NavSlot
            icon={<BoltIcon sx={ICON_SX} />}
            label="Instant Wins"
            href={`/${page.company_domain}/prize-draw?filter=${INSTANT_WINS_FILTER}`}
            isActive={isInstantWinsActive}
          />
        )}
        {isInstantWinsPending && <Box aria-hidden />}
        <NavSlot
          icon={<TrackChangesOutlinedIcon sx={ICON_SX} />}
          label="Predictor"
          href={`/${page.company_domain}/landing`}
          isActive={isPredictorActive}
          accent={PREDICTOR_ACCENT}
        />
        {renderAuthSlots()}
      </Box>
    </>
  )
}
